/**
 * Quiz Component
 * Handles multi-step quiz navigation and form submission.
 * Data usage: hours × MB/hour × days per activity, then summed (MB → GB).
 * MB/hour: Browsing 17, Social 520, Music 72, Gaming 80, TV 2400.
 */

const QUIZ_MB_PER_HOUR = {
  browsing: 17,
  social: 520,
  music: 72,
  gaming: 80,
  tv: 2400,
};
const KLAVIYO_CLIENT_SUBSCRIPTIONS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_API_REVISION = '2026-01-15';

/** Fallback auto-dismiss delay (ms) when the user doesn't click "Continue". */
const ERROR_AUTO_DISMISS_MS = 5000;

if (!customElements.get('quiz-container')) {
  customElements.define(
    'quiz-container',
    class QuizContainer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id');
        this.steps = Array.from(this.querySelectorAll('.quiz__step'));
        this.currentStepIndex = 0;
        this.answers = {};
        this.formStep = this.querySelector('.quiz__step--form');
        // Total steps excludes the form step
        this.totalSteps = this.steps.filter(step => !step.classList.contains('quiz__step--form')).length;

        this.nextButton = this.querySelector('[data-quiz-next]');
        this.prevButton = this.querySelector('[data-quiz-prev]');
        this.progressText = this.querySelector('[data-progress-text]');
        this.dataEstimateElements = this.querySelectorAll('[data-quiz-data-estimate]');
        this.dataProgressElements = this.querySelectorAll('[data-quiz-data-progress]');
        this.quizForm = this.querySelector('[data-quiz-form]');
        this.emailInput = this.querySelector('[data-quiz-email]');
        this.formError = this.querySelector('[data-quiz-form-error]');
        this.formSuccess = this.querySelector('[data-quiz-form-success]');
        this.progressMaxGb = parseFloat(this.getAttribute('data-quiz-progress-max')) || 250;
        this.klaviyoCompanyId = (
          this.getAttribute('data-klaviyo-company-id') || ''
        ).trim();
        this.klaviyoListId = (this.getAttribute('data-klaviyo-list-id') || '').trim();
        this.subscriptionFailureBehavior =
          this.getAttribute('data-quiz-on-failure') || 'block';

        this.messages = {
          invalidEmail: this.getAttribute('data-msg-invalid-email') || 'Please enter a valid email address',
          genericError: this.getAttribute('data-msg-generic-error') || 'An error occurred. Please try again.',
          networkError: this.getAttribute('data-msg-network-error') || 'Unable to submit your email right now. Please try again.',
          subscriptionFailed: this.getAttribute('data-msg-subscription-failed') || 'Subscription failed. Please try again.',
          continueButton: this.getAttribute('data-msg-continue-button') || 'Continue',
        };

        this.init();
      }

      init() {
        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.handleNext.bind(this));
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', this.handlePrev.bind(this));
        }

        if (this.quizForm) {
          this.quizForm.addEventListener('submit', this.handleSubmit.bind(this));
        }

        // Listen for answer changes
        this.addEventListener('change', this.handleAnswerChange.bind(this));

        this.updateProgress();
        this.updateNavigation();
        this.updateDataEstimate();

      }

      handleNext() {
        if (!this.validateCurrentStep()) {
          return;
        }

        this.saveCurrentStepAnswers();
        this.updateDataEstimate();

        const currentStep = this.steps[this.currentStepIndex];
        const isLastQuizStep = !currentStep.classList.contains('quiz__step--form') && 
                               (this.currentStepIndex === this.totalSteps - 1);

        // If we're on the last quiz step, go to form step
        if (isLastQuizStep && this.formStep) {
          const formStepIndex = this.steps.indexOf(this.formStep);
          if (formStepIndex !== -1) {
            this.currentStepIndex = formStepIndex;
            this.showStep(this.currentStepIndex);
            this.updateProgress();
            this.updateNavigation();
          }
        } else if (this.currentStepIndex < this.steps.length - 1) {
          // Regular step progression
          this.currentStepIndex++;
          this.showStep(this.currentStepIndex);
          this.updateProgress();
          this.updateNavigation();
          this.updateDataEstimate();
        }
      }

      handlePrev() {
        if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.showStep(this.currentStepIndex);
          this.updateProgress();
          this.updateNavigation();
          this.updateDataEstimate();
        }
      }

      showStep(index) {
        this.steps.forEach((step, i) => {
          if (i === index) {
            step.classList.remove('hidden');
            step.setAttribute('aria-hidden', 'false');

            // Focus first input in step
            const firstInput = step.querySelector('input, textarea, select');
            if (firstInput && !firstInput.disabled) {
              setTimeout(() => firstInput.focus(), 100);
            }
          } else {
            step.classList.add('hidden');
            step.setAttribute('aria-hidden', 'true');
          }
        });
      }

      validateCurrentStep() {
        const currentStep = this.steps[this.currentStepIndex];
        if (!currentStep || currentStep.classList.contains('quiz__step--form')) {
          return true;
        }

        const requiredInputs = currentStep.querySelectorAll('[required]');
        let isValid = true;

        requiredInputs.forEach((input) => {
          if (!input.value || (input.type === 'checkbox' && !input.checked)) {
            isValid = false;
            input.classList.add('invalid');
          } else {
            input.classList.remove('invalid');
          }
        });

        // Check if at least one answer is selected for single choice
        const radioGroup = currentStep.querySelector('input[type="radio"]');
        if (radioGroup && !currentStep.querySelector('input[type="radio"]:checked')) {
          isValid = false;
          const radioInputs = currentStep.querySelectorAll('input[type="radio"]');
          radioInputs.forEach((input) => input.classList.add('invalid'));
        }

        return isValid;
      }

      saveCurrentStepAnswers() {
        const currentStep = this.steps[this.currentStepIndex];
        if (!currentStep || currentStep.classList.contains('quiz__step--form')) {
          return;
        }

        const stepId = currentStep.getAttribute('data-step-id');
        const stepType = currentStep.getAttribute('data-step-type') || 'browsing';

        // Handle single choice (radio)
        const selectedRadio = currentStep.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          const numericVal = parseFloat(selectedRadio.getAttribute('data-value')) ||
            parseFloat(selectedRadio.getAttribute('data-weight')) || 0;
          this.answers[stepId] = {
            value: selectedRadio.value,
            weight: parseFloat(selectedRadio.getAttribute('data-weight')) || 0,
            optionIndex: parseInt(selectedRadio.getAttribute('data-option-index')) || 0,
            stepType,
            numericValue: numericVal
          };
        }

        // Handle multiple choice (checkbox)
        const selectedCheckboxes = currentStep.querySelectorAll('input[type="checkbox"]:checked');
        if (selectedCheckboxes.length > 0) {
          const items = Array.from(selectedCheckboxes).map((cb) => {
            const numericVal = parseFloat(cb.getAttribute('data-value')) ||
              parseFloat(cb.getAttribute('data-weight')) || 0;
            return {
              value: cb.value,
              weight: parseFloat(cb.getAttribute('data-weight')) || 0,
              numericValue: numericVal
            };
          });
          this.answers[stepId] = { stepType, items };
        }

        // Handle text input (no stepType / numericValue for formula)
        const textInput = currentStep.querySelector('input[type="text"]:not([type="radio"]):not([type="checkbox"])');
        if (textInput && textInput.value) {
          this.answers[stepId] = {
            value: textInput.value.trim(),
            weight: 0,
            optionIndex: 0,
            stepType: null,
            numericValue: 0
          };
        }

        this.updateDataEstimate();
      }

      calculateTotalDataUsage() {
        let days = 0;
        let totalMB = 0;
        const activityTypes = ['browsing', 'social', 'music', 'gaming', 'tv'];

        Object.values(this.answers).forEach((answer) => {
          if (!answer || typeof answer !== 'object') return;

          if (answer.stepType === 'days') {
            const val = answer.numericValue;
            if (typeof val === 'number' && !Number.isNaN(val)) days = val;
            return;
          }

          if (answer.items) {
            // Multiple choice: sum numericValue as hours for this activity
            const hours = answer.items.reduce((sum, it) => sum + (it.numericValue || 0), 0);
            const mb = QUIZ_MB_PER_HOUR[answer.stepType];
            if (mb != null && days > 0) totalMB += hours * mb * days;
            return;
          }

          if (activityTypes.includes(answer.stepType)) {
            const hours = answer.numericValue || 0;
            const mb = QUIZ_MB_PER_HOUR[answer.stepType];
            if (mb != null && days > 0) totalMB += hours * mb * days;
          }
        });

        const totalGB = totalMB / 1000;
        return Math.round(totalGB * 10) / 10;
      }

      calculateDataRange() {
        const total = this.calculateTotalDataUsage();
        const min = Math.max(0, Math.round(total * 0.85));
        const max = Math.round(total * 1.15);
        return { min, max };
      }

      updateDataEstimate() {
        const total = this.calculateTotalDataUsage();
        const currentStep = this.steps[this.currentStepIndex];
        
        if (!currentStep || currentStep.classList.contains('quiz__step--form')) {
          return;
        }

        // Update the data estimate display for current step
        const estimateElement = currentStep.querySelector('[data-quiz-data-estimate]');
        const progressElement = currentStep.querySelector('[data-quiz-data-progress]');
        
        if (estimateElement) {
          estimateElement.textContent = `${total} GB`;
        }

        if (progressElement && this.progressMaxGb > 0) {
          const percentage = Math.min(100, (total / this.progressMaxGb) * 100);
          progressElement.style.width = `${percentage}%`;
        }
      }

      handleAnswerChange(event) {
        const input = event.target;
        if (input.hasAttribute('data-quiz-answer')) {
          input.classList.remove('invalid');

          // Auto-save for immediate feedback
          const currentStep = this.steps[this.currentStepIndex];
          if (currentStep && !currentStep.classList.contains('quiz__step--form')) {
            this.saveCurrentStepAnswers();
            this.updateDataEstimate();
          }
        }
      }

      updateProgress() {
        if (!this.progressText) {
          return;
        }

        const currentStep = this.steps[this.currentStepIndex];
        const isFormStep = currentStep?.classList.contains('quiz__step--form');
        const progressContainer = this.querySelector('[data-quiz-progress]');

        // Hide progress text on form step
        if (progressContainer) {
          if (isFormStep) {
            progressContainer.setAttribute('data-quiz-progress-hidden', '');
          } else {
            progressContainer.removeAttribute('data-quiz-progress-hidden');
          }
        }

        let stepNumber;

        if (isFormStep) {
          stepNumber = this.totalSteps;
        } else {
          stepNumber = this.currentStepIndex + 1;
        }

        if (this.progressText) {
          this.progressText.textContent = stepNumber;
        }
      }

      updateNavigation() {
        const currentStep = this.steps[this.currentStepIndex];
        const isFirstStep = this.currentStepIndex === 0;
        const isFormStep = currentStep?.classList.contains('quiz__step--form');
        const resultView = this.querySelector('.quiz__result-view');
        const isResultViewVisible = resultView && !resultView.classList.contains('hidden');

        // Hide buttons on form step or result view
        if (isFormStep || isResultViewVisible) {
          if (this.prevButton) {
            this.prevButton.hidden = true;
          }
          if (this.nextButton) {
            this.nextButton.hidden = true;
          }
          return;
        }

        // Previous button - visible but with reduced opacity on first step
        if (this.prevButton) {
          if (isFirstStep) {
            this.prevButton.hidden = false;
            this.prevButton.classList.add('quiz__button--disabled');
            this.prevButton.disabled = true;
          } else {
            this.prevButton.hidden = false;
            this.prevButton.classList.remove('quiz__button--disabled');
            this.prevButton.disabled = false;
          }
        }

        // Next button
        if (this.nextButton) {
          this.nextButton.hidden = false;
        }
      }

      async handleSubmit(event) {
        event.preventDefault();

        const email = this.emailInput ? this.emailInput.value.trim() : '';
        if (!email) {
          this.showFormError(this.messages.invalidEmail);
          return;
        }
        this.emailInput.value = email;
        if (!this.emailInput.checkValidity()) {
          this.showFormError(this.messages.invalidEmail);
          return;
        }

        // Save all answers before submission (restore current index after)
        const originalIndex = this.currentStepIndex;
        this.steps.forEach((step, index) => {
          if (!step.classList.contains('quiz__step--form')) {
            this.currentStepIndex = index;
            this.saveCurrentStepAnswers();
          }
        });
        this.currentStepIndex = originalIndex;

        // Calculate data estimate range
        const dataRange = this.calculateDataRange();

        // Hide error messages
        if (this.formError) {
          this.formError.classList.add('hidden');
        }

        this.setSubmitButtonLoading(true);
        try {
          const subscriptionResult = await this.subscribeEmailToKlaviyo(email);
          if (!subscriptionResult.success) {
            if (this.subscriptionFailureBehavior !== 'continue') {
              this.showFormError(subscriptionResult.message);
              return;
            }

            await this.showFormError(subscriptionResult.message, {
              showContinue: true,
            });
          }

          this.showResultView(dataRange);
        } finally {
          this.setSubmitButtonLoading(false);
        }
      }

      /**
       * Displays an error message inside the form error banner.
       * @param {string} message - Error text to show.
       * @param {object} [options] - Display options.
       * @param {boolean} [options.showContinue=false] - Append a dismiss button.
       * @returns {Promise<void>|undefined} Resolves when the user
       *   clicks "Continue" or after ERROR_AUTO_DISMISS_MS, whichever
       *   comes first. Returns undefined when showContinue is false.
       */
      showFormError(message, { showContinue = false } = {}) {
        if (!this.formError) return undefined;

        this.formError.textContent = '';
        this.formError.setAttribute('role', 'alert');

        const text = document.createElement('span');
        text.textContent = message;
        this.formError.appendChild(text);

        this.formError.classList.remove('hidden');

        if (!showContinue) return undefined;

        return new Promise((resolve) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quiz__form-error-dismiss';
          btn.textContent = this.messages.continueButton;
          this.formError.appendChild(btn);

          const timer = setTimeout(resolve, ERROR_AUTO_DISMISS_MS);
          btn.addEventListener(
            'click',
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }

      showFormSuccess(message) {
        if (this.formSuccess) {
          this.formSuccess.textContent = message;
          this.formSuccess.classList.remove('hidden');
          this.formSuccess.setAttribute('role', 'status');
        }
        if (this.formError) {
          this.formError.classList.add('hidden');
        }
      }

      setSubmitButtonLoading(isLoading) {
        const submitButton = this.quizForm && this.quizForm.querySelector('button[type="submit"]');
        if (!submitButton) return;

        if (isLoading) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('aria-busy', 'true');
          submitButton.disabled = true;
          return;
        }

        submitButton.setAttribute('aria-disabled', 'false');
        submitButton.removeAttribute('aria-busy');
        submitButton.disabled = false;
      }

      async subscribeEmailToKlaviyo(email) {
        if (!this.klaviyoCompanyId || !this.klaviyoListId) {
          console.error(
            'Klaviyo company ID and list ID must be set in section settings.',
          );
          return {
            success: false,
            message: this.messages.genericError,
          };
        }

        const endpoint = `${KLAVIYO_CLIENT_SUBSCRIPTIONS_ENDPOINT}?company_id=${encodeURIComponent(this.klaviyoCompanyId)}`;
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.klaviyoListId,
                },
              },
            },
          },
        };

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Revision: KLAVIYO_API_REVISION,
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            return { success: true };
          }

          const responseBody = await response.json().catch(() => null);
          return {
            success: false,
            message: this.getKlaviyoErrorMessage(responseBody),
          };
        } catch {
          return {
            success: false,
            message: this.messages.networkError,
          };
        }
      }

      getKlaviyoErrorMessage(responseBody) {
        if (!responseBody || !Array.isArray(responseBody.errors) || responseBody.errors.length === 0) {
          return this.messages.subscriptionFailed;
        }

        const firstError = responseBody.errors[0] || {};
        return (
          firstError.detail ||
          firstError.title ||
          this.messages.subscriptionFailed
        );
      }

      showResultView(dataRange) {
        // Hide form step
        if (this.formStep) {
          this.formStep.classList.add('hidden');
        }

        // Hide navigation
        const navigation = this.querySelector('.quiz__navigation');
        if (navigation) {
          navigation.classList.add('hidden');
        }

        // Create or show result view
        let resultView = this.querySelector('.quiz__result-view');
        if (!resultView) {
          resultView = document.createElement('div');
          resultView.className = 'quiz__result-view';
          resultView.innerHTML = this.getResultViewHTML(dataRange);
          this.querySelector('.quiz__content').appendChild(resultView);
        } else {
          resultView.classList.remove('hidden');
          resultView.innerHTML = this.getResultViewHTML(dataRange);
        }

        // Hide navigation buttons when result view is shown
        this.updateNavigation();

        // Scroll to result view
        resultView.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      /**
       * Escapes a string for safe use in HTML attributes and text content.
       * @param {string} str - Raw string from section settings
       * @returns {string} HTML-safe string
       */
      escapeHtml(str) {
        if (str == null || str === '') return '';
        const m = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return String(str).replace(/[&<>"']/g, (c) => m[c]);
      }

      getResultViewHTML(dataRange) {
        const btnUrl = this.getAttribute('data-quiz-result-button-url') || '/pages/plans';
        const btnText = this.getAttribute('data-quiz-result-button-text') || 'view all plans';
        return `
          <div class="quiz__result-content">
            <div class="quiz__result-header">
              <h3 class="quiz__result-subtitle">your results are in</h3>
              <h2 class="quiz__result-title">${dataRange.min}-${dataRange.max}GB</h2>
              <p class="quiz__result-description">
                your estimated use is somewhere between <strong>${dataRange.min}-${dataRange.max}GB per month.</strong>
                <br>Below are suggested data plan packages.
              </p>
            </div>
            <div class="quiz__result-plans">
              ${this.getSuggestedPlansHTML(dataRange)}
            </div>
            <a href="${this.escapeHtml(btnUrl)}" class="button button--primary button--sm quiz__result-button">
              <span class="btn-fill" data-fill="" style="transform: translateY(var(--motion-translateY)); --motion-translateY: -76%;"></span>
              <span class="btn-text">
                <span>
                  ${this.escapeHtml(btnText)}
                </span>
              </span>
            </a>
          </div>
        `;
      }

      getSuggestedPlansHTML(dataRange) {
        const plansTemplate = this.querySelector('[data-quiz-data-plans-template]');
        if (!plansTemplate) return this.getDefaultPlansHTML();

        const planItems = plansTemplate.querySelectorAll('.quiz__data-plan-item');
        if (planItems.length === 0) return this.getDefaultPlansHTML();

        const estimatedData = dataRange.max || dataRange.min || 0;
        const regularPlans = [];
        let unlimitedPlan = null;

        planItems.forEach((planItem) => {
          if (planItem.getAttribute('data-quiz-plan-type') === 'unlimited') {
            unlimitedPlan = planItem.cloneNode(true);
          } else {
            regularPlans.push(planItem);
          }
        });

        regularPlans.sort((a, b) => {
          const sizeA = parseFloat(a.getAttribute('data-quiz-plan-size')) || 0;
          const sizeB = parseFloat(b.getAttribute('data-quiz-plan-size')) || 0;
          return sizeA - sizeB;
        });

        // 1. Suggested: smallest plan that covers usage (first plan > estimate)
        let suggestedPlan = null;
        let suggestedIndex = -1;
        const largestRegularSize = regularPlans.length > 0
          ? parseFloat(regularPlans[regularPlans.length - 1].getAttribute('data-quiz-plan-size')) || 0
          : 0;

        for (let i = 0; i < regularPlans.length; i++) {
          const size = parseFloat(regularPlans[i].getAttribute('data-quiz-plan-size')) || 0;
          if (size > estimatedData) {
            suggestedPlan = regularPlans[i].cloneNode(true);
            suggestedIndex = i;
            break;
          }
        }
        if (!suggestedPlan && regularPlans.length > 0) {
          suggestedIndex = regularPlans.length - 1;
          suggestedPlan = regularPlans[suggestedIndex].cloneNode(true);
        }

        // If estimate exceeds all regular plans, suggest unlimited
        let suggestedIsUnlimited = false;
        if (unlimitedPlan && estimatedData > largestRegularSize) {
          suggestedPlan = unlimitedPlan.cloneNode(true);
          suggestedIsUnlimited = true;
        }

        // 2. Step-down: next smaller plan below suggested (budget-friendly)
        let stepDownPlan = null;
        if (suggestedIsUnlimited && regularPlans.length > 0) {
          stepDownPlan = regularPlans[regularPlans.length - 1].cloneNode(true);
        } else if (!suggestedIsUnlimited && suggestedIndex > 0) {
          stepDownPlan = regularPlans[suggestedIndex - 1].cloneNode(true);
        }

        const plansToDisplay = [];

        if (stepDownPlan) {
          const stepDownEl = stepDownPlan.querySelector('.quiz__plan');
          if (stepDownEl && !stepDownEl.querySelector('.quiz__plan-header')) {
            const header = document.createElement('div');
            header.className = 'quiz__plan-header quiz__plan-header--step-down';
            header.innerHTML = '';
            const body = stepDownEl.querySelector('.quiz__plan-body');
            if (body) stepDownEl.insertBefore(header, body);
          }
          plansToDisplay.push(stepDownPlan);
        }

        if (suggestedPlan) {
          const planEl = suggestedPlan.querySelector('.quiz__plan');
          if (planEl) {
            planEl.classList.add('quiz__plan--featured');
            const badgeLabel = (
              this.getAttribute('data-quiz-plan-badge-label') || ''
            ).trim();
            if (
              badgeLabel &&
              !planEl.querySelector('.quiz__plan-header')
            ) {
              const header = document.createElement('div');
              header.className = 'quiz__plan-header';
              const badge = document.createElement('span');
              badge.className = 'quiz__plan-badge';
              badge.textContent = badgeLabel;
              header.appendChild(badge);
              const body = planEl.querySelector('.quiz__plan-body');
              if (body) planEl.insertBefore(header, body);
            }
          }
          plansToDisplay.push(suggestedPlan);
        }

        if (unlimitedPlan && !suggestedIsUnlimited) plansToDisplay.push(unlimitedPlan.cloneNode(true));

        if (plansToDisplay.length === 0) return this.getDefaultPlansHTML();
        return plansToDisplay.map((p) => p.outerHTML).join('');
      }

      getDefaultPlansHTML() {
        // Fallback to default plans if no metaobjects are configured
        const badgeLabel = (
          this.getAttribute('data-quiz-plan-badge-label') || ''
        ).trim();
        const badgeHtml = badgeLabel
          ? `<div class="quiz__plan-header"><span class="quiz__plan-badge">${this.escapeHtml(badgeLabel)}</span></div>`
          : '';
        return `
          <div class="quiz__plan quiz__plan--featured">
            ${badgeHtml}
            <div class="quiz__plan-body">
              <h4 class="quiz__plan-title">100GB</h4>
              <div class="quiz__plan-price">
                <span class="quiz__plan-price-amount">$89</span>
                <span class="quiz__plan-price-period">/Mo</span>
              </div>
              <p class="quiz__plan-subtitle">Only when you need it</p>
              <ul class="quiz__plan-features">
                <li>4G LTE connection</li>
                <li>Multi-network coverage</li>
                <li>Endless hours of HD streaming</li>
                <li>Unlimited Internet & Social access</li>
              </ul>
              <div class="quiz__plan-best-for">
                <strong>Best for:</strong> Full-time, 4K streaming, remote work & gaming.
              </div>
            </div>
          </div>
          <div class="quiz__plan">
            <div class="quiz__plan-body">
              <h4 class="quiz__plan-title">500GB</h4>
              <div class="quiz__plan-price">
                <span class="quiz__plan-price-amount">$69</span>
                <span class="quiz__plan-price-period">/Mo</span>
              </div>
              <p class="quiz__plan-subtitle">Only when you need it</p>
              <ul class="quiz__plan-features">
                <li>4G LTE connection</li>
                <li>Multi-network coverage</li>
                <li>1-2 hours of HD streaming</li>
                <li>10 hours browsing & social</li>
              </ul>
              <div class="quiz__plan-best-for">
                <strong>Best for:</strong> Security systems, email & light browsing.
              </div>
            </div>
          </div>
          <div class="quiz__plan">
            <div class="quiz__plan-body">
              <h4 class="quiz__plan-title">Unlimited</h4>
              <div class="quiz__plan-price">
                <span class="quiz__plan-price-amount">$129</span>
                <span class="quiz__plan-price-period">/Mo</span>
              </div>
              <p class="quiz__plan-subtitle">Only when you need it</p>
              <ul class="quiz__plan-features">
                <li>4G LTE connection</li>
                <li>Multi-network coverage</li>
                <li>1-2 hours of HD streaming</li>
                <li>10 hours browsing & social</li>
              </ul>
              <div class="quiz__plan-best-for">
                <strong>Best for:</strong> Security systems, email & light browsing.
              </div>
            </div>
          </div>
        `;
      }
    }
  );
}

